import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, ChevronDownIcon, CheckIcon } from 'lucide-react';
import { COUNTRIES } from '../utils/calculations';
import type { CountryCode } from '../utils/calculations';
interface CountrySelectorProps {
  selected: CountryCode;
  onChange: (code: CountryCode) => void;
}
export function CountrySelector({
  selected,
  onChange
}: CountrySelectorProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const current = COUNTRIES.find(c => c.code === selected)!;
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  return <div className="flex items-center gap-3 justify-end mb-2" ref={ref} data-id="element-36">
      <span className="text-xs font-semibold text-white/40 uppercase tracking-widest hidden sm:block" data-id="element-37">
        Mercado Operativo:
      </span>

      <div className="relative" data-id="element-38">
        <button onClick={() => setOpen(!open)} className="
            flex items-center gap-2.5 px-4 py-2 rounded-xl
            bg-transparent border border-salmon/30
            hover:border-salmon/60 hover:shadow-[0_0_15px_rgba(242,167,167,0.15)]
            focus:outline-none focus:border-salmon/60
            transition-all duration-200 group
          " data-id="element-39">
          <Globe className="w-4 h-4 text-salmon/70 group-hover:text-salmon transition-colors" data-id="element-40" />
          <span className="text-lg leading-none" data-id="element-41">{current.flag}</span>
          <span className="text-sm font-bold text-white" data-id="element-42">{current.name}</span>
          <span className="text-xs font-medium text-white/40" data-id="element-43">
            ({current.currency})
          </span>
          <ChevronDownIcon className={`w-4 h-4 text-white/40 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} data-id="element-44" />
        </button>

        <AnimatePresence data-id="element-45">
          {open && <motion.div initial={{
          opacity: 0,
          y: -8,
          scale: 0.96
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: -8,
          scale: 0.96
        }} transition={{
          duration: 0.15
        }} className="
                absolute right-0 top-full mt-2 w-64 z-50
                bg-black/95 backdrop-blur-xl border border-white/10 rounded-xl
                shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden
              " data-id="element-46">
              {COUNTRIES.map(country => {
            const isSelected = country.code === selected;
            return <button key={country.code} onClick={() => {
              onChange(country.code);
              setOpen(false);
            }} className={`
                      w-full flex items-center gap-3 px-4 py-3 text-left transition-all
                      ${isSelected ? 'bg-salmon/10 border-l-2 border-salmon' : 'hover:bg-white/5 border-l-2 border-transparent'}
                    `} data-id="element-47">
                    <span className="text-xl leading-none" data-id="element-48">{country.flag}</span>
                    <div className="flex-grow" data-id="element-49">
                      <div className={`text-sm font-bold ${isSelected ? 'text-white' : 'text-white/70'}`} data-id="element-50">
                        {country.name}
                      </div>
                      <div className="text-xs text-white/40 font-medium" data-id="element-51">
                        {country.currency} — {country.symbol}
                      </div>
                    </div>
                    {isSelected && <CheckIcon className="w-4 h-4 text-salmon" data-id="element-52" />}
                  </button>;
          })}
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}